import { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const haptic = () => navigator.vibrate?.(8);

export const ReflectionChat = ({ accent, world }) => {
  const [messages, setMessages] = useState([]);
  const [session, setSession] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || sending) return;
    haptic();
    setText("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/alignment/chat", { message: msg, session_id: session });
      setSession(data.session_id);
      setMessages(data.messages);
    } catch (err) {
      toast.error(apiErr(err));
      setMessages((m) => [...m, { role: "assistant", text: "The guide is resting. Try again in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="reflection-chat" className="flex flex-col h-[380px]">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.length === 0 && (
          <p className="text-sm text-slate-400 py-6 text-center">
            Share what&rsquo;s on your mind. Aria will reflect with you on your path through {world.name}.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              data-testid={`chat-msg-${m.role}`}
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "text-slate-900" : "bg-white/[0.06] text-slate-200 border border-white/10"}`}
              style={m.role === "user" ? { background: accent } : {}}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl px-4 py-2.5 bg-white/[0.06] border border-white/10 text-slate-400 inline-flex items-center gap-2 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> reflecting…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="mt-3 flex gap-2">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a thought…"
          className="flex-1 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-slate-100 text-sm placeholder:text-slate-500 focus:border-white/30 outline-none transition-colors duration-300"
        />
        <button
          data-testid="chat-send-btn"
          type="submit"
          disabled={sending || !text.trim()}
          className="rounded-xl px-4 text-slate-900 transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
          style={{ background: accent }}
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};
